import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SiteNav, MinimalFooter, useStartAnfrage } from '../shared';
import Seo from '../Seo';
import { trackMeta } from '../../lib/metaTrack';
import { sendLead } from '../../lib/emailLead';
import { berichtAnfordern, orteZurPlz, bezirkFuerStrasse, fristAbfragen } from '../../lib/mietwerte/api';
import {
  OBERARTEN, UNTERARTEN, GEWERBEANTEIL, Piktogramm, hinweisZu,
} from './objektarten';

/* -------------------------------------------------------------------------
   Kostenlose Mietpreis-Analyse — fünfstufiger Fragebogen, der zugleich die
   Daten für den Objektreport einsammelt.

   Die Objektart steht am Anfang und verzweigt alles Weitere: Welche Felder
   erscheinen, ob überhaupt gerechnet werden kann und mit welchem Vorbehalt das
   Ergebnis zu lesen ist. Reine Gewerbeobjekte gehen an den Gebäude- und
   Mietfragen vorbei direkt zum Kontakt — dort rechnet ein Mensch.

   Rechnung und Datensatz liegen im Backend; hier wird nur gefragt und gezeigt.
   ------------------------------------------------------------------------- */

const SCHRITTE = ['Nutzung', 'Lage', 'Eckdaten', 'Miete', 'Kontakt'];

const ERHOEHUNG = [
  { id: 'nie', titel: 'Noch nie' },
  { id: 'unbekannt', titel: 'Weiß ich nicht' },
  { id: 'ein_bis_drei', titel: 'Vor 1-3 Jahren' },
  { id: 'ueber_drei', titel: 'Vor >3 Jahren' },
];

const ROLLE = ['Eigentümer', 'Kaufinteressent', 'Sonstiges'];
const ANREDE = ['Herr', 'Frau'];

// Plausibilitätsgrenzen. Sie fangen die haeufigsten Eingabefehler ab:
// Jahres- statt Monatsmiete und Werte je Wohnung statt Summe.
const QM_JE_WE = { min: 15, max: 400 };
const MIETE_JE_QM = { min: 2, max: 45 };

const LEER = {
  oberart: '', objektart: '', gewerbeanteil: '',
  strasse: '', plz: '', ags: '', bezirk: '',
  baujahr: '', einheiten: '', wohnflaeche: '',
  gewerbeeinheiten: '', gewerbeflaeche: '', gewerbemiete: '',
  kaltmiete: '', erhoehungArt: '', erhoehungJahr: '', erhoehungProzent: '', leerstand: '',
  rolle: '', anrede: '', vorname: '', nachname: '', email: '', telefon: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const JAHRE = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

const eur = (n) => Math.round(n).toLocaleString('de-DE');
// Geschütztes Leerzeichen vor der Einheit: sonst rutscht in schmalen Spalten
// das „€“ allein in die nächste Zeile.
const eurE = (n) => `${eur(n)}\u00A0€`;
const qm = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qmE = (n) => `${qm(n)}\u00A0€/m²`;

// Beträge werden beim Tippen gruppiert: aus 9800 wird 9.800. Gespeichert wird
// die formatierte Zeichenkette, gerechnet wird mit `zahl()`.
const alsBetrag = (roh) => {
  const ziffern = String(roh).replace(/\D/g, '').slice(0, 9);
  return ziffern ? Number(ziffern).toLocaleString('de-DE') : '';
};
const zahl = (v) => Number(String(v).replace(/\./g, '').replace(',', '.'));

function Feld({ label, hint, error, pflicht, children }) {
  return (
    <div className={`field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{pflicht && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

/** Kacheln mit Piktogramm — für die verzweigenden Fragen. */
function Kacheln({ optionen, wert, onChange, spalten }) {
  return (
    <div className="mpa-kacheln" data-spalten={spalten || optionen.length}>
      {optionen.map((o) => (
        <button
          key={o.id} type="button"
          className={`mpa-kachel ${wert === o.id ? 'aktiv' : ''}`}
          aria-pressed={wert === o.id}
          onClick={() => onChange(o.id)}
        >
          <span className="mpa-kachel-icon"><Piktogramm art={o.id} /></span>
          <span className="mpa-kachel-titel">{o.titel}</span>
        </button>
      ))}
    </div>
  );
}

/** Kacheln ohne Piktogramm — für Fragen mit kurzen, klaren Antworten. */
function Wahl({ optionen, wert, onChange }) {
  return (
    <div className="mpa-wahl">
      {optionen.map((o) => {
        const id = o.id || o;
        return (
          <button
            key={id} type="button"
            className={`mpa-wahl-opt ${wert === id ? 'aktiv' : ''}`}
            aria-pressed={wert === id}
            onClick={() => onChange(id)}
          >
            {o.titel || o}
          </button>
        );
      })}
    </div>
  );
}

function Band({ ton, kopf, text }) {
  return (
    <p className={`mpa-band ${ton === 'warn' ? 'mpa-band--warn' : ''}`}>
      <strong>{kopf}</strong> {text}
    </p>
  );
}

/**
 * Das Werkzeug selbst — ohne Navigation, Fusszeile und Seitenkopf, damit es
 * sowohl unter eigener Adresse laufen als auch in eine andere Seite eingesetzt
 * werden kann (`eingebettet`). Eingebettet aendert sich dreierlei: kein
 * Freiraum fuer die Navigation, die Ueberschriften ruecken eine Stufe tiefer
 * (die Gastseite hat bereits ihre h1), und Spruenge gehen an den Anfang des
 * Werkzeugs statt an den Seitenanfang.
 */
export function MietpreisAnalyseTool({ eingebettet = false }) {
  const startAnfrage = useStartAnfrage();
  const [schritt, setSchritt] = useState(1);
  const [form, setForm] = useState(LEER);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState({});
  const [status, setStatus] = useState('idle');   // idle | sendet | fertig | fehler
  const [ergebnis, setErgebnis] = useState(null);
  const [ortAnzeige, setOrtAnzeige] = useState('');
  const [bezirkStatus, setBezirkStatus] = useState(null);
  const abbruch = useRef(null);
  const gestartet = useRef(false);

  const setzen = (k) => (v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFehler((x) => ({ ...x, [k]: null }));
  };
  const ausEvent = (k) => (e) => setzen(k)(e.target.value);
  const alsBetragSetzen = (k) => (e) => setzen(k)(alsBetrag(e.target.value));

  const istGewerbe = form.oberart === 'gewerbe';
  const istMfh = form.objektart === 'mfh';
  const hatGewerbe = istMfh && form.gewerbeanteil && form.gewerbeanteil !== 'kein';
  const flaechenLabel = istGewerbe ? 'Gewerbefläche in m²'
    : istMfh ? 'Wohnfläche in m² (alle Wohnungen)' : 'Wohnfläche in m²';

  // Erste Ebene wechseln setzt die zweite zurück, sonst bleibt eine
  // unpassende Kombination stehen.
  const oberartWaehlen = (id) => setForm((f) => ({
    ...f, oberart: id, objektart: '', gewerbeanteil: '',
  }));
  const unterartWaehlen = (id) => setForm((f) => ({
    ...f, objektart: id, gewerbeanteil: id === 'mfh' ? f.gewerbeanteil : '',
  }));

  const hinweis = hinweisZu(form.objektart, form.gewerbeanteil);

  // --- Ortsauflösung -------------------------------------------------------
  const [ortDaten, setOrtDaten] = useState({ orte: [], bezirke: [] });
  useEffect(() => {
    if (!/^\d{5}$/.test(form.plz)) { setOrtDaten({ orte: [], bezirke: [] }); return undefined; }
    const ac = new AbortController();
    setLaedt(true);
    orteZurPlz(form.plz, { signal: ac.signal })
      .then((d) => { setOrtDaten(d); setLaedt(false); })
      .catch(() => setLaedt(false));
    return () => ac.abort();
  }, [form.plz]);

  const orte = ortDaten.orte;
  const bezirksKandidaten = ortDaten.bezirke || [];

  useEffect(() => {
    if (orte.length && !orte.some((o) => o.ags === form.ags)) setzen('ags')(orte[0].ags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orte]);

  // Straße -> Stadtbezirk, entprellt. Läuft über unseren Server, damit die
  // Adresse nicht vom Browser aus an einen Dritten geht.
  useEffect(() => {
    if (!form.ags || !bezirksKandidaten.length) { setBezirkStatus(null); return undefined; }
    if (form.strasse.trim().length < 4) { setBezirkStatus(null); return undefined; }
    const t = setTimeout(async () => {
      if (abbruch.current) abbruch.current.abort();
      abbruch.current = new AbortController();
      setBezirkStatus('suche');
      const r = await bezirkFuerStrasse(form.strasse, form.plz, form.ags,
        { signal: abbruch.current.signal });
      setBezirkStatus(r);
      if (r) setzen('bezirk')(r.bezirk);
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.strasse, form.plz, form.ags, bezirksKandidaten.length]);

  // --- Fristen -------------------------------------------------------------
  const [frist, setFrist] = useState({ im_fenster: false, jahr_noetig: false });
  useEffect(() => {
    if (!form.erhoehungArt) { setFrist({ im_fenster: false, jahr_noetig: false }); return undefined; }
    const ac = new AbortController();
    fristAbfragen({ art: form.erhoehungArt, jahr: form.erhoehungJahr }, { signal: ac.signal })
      .then(setFrist).catch(() => {});
    return () => ac.abort();
  }, [form.erhoehungArt, form.erhoehungJahr]);

  // Laufende Kontrolle unter dem Mietfeld: Wer sich vertippt, sieht die Folge
  // sofort als €/m², statt sie erst im Ergebnis zu entdecken.
  const proQmVorschau = useMemo(() => {
    const f = zahl(form.wohnflaeche);
    const m = zahl(form.kaltmiete);
    return f > 0 && m > 0 ? m / f : null;
  }, [form.wohnflaeche, form.kaltmiete]);

  // --- Validierung ---------------------------------------------------------
  function pruefe(s) {
    const e = {};
    if (s === 1) {
      if (!form.oberart) e.oberart = 'Bitte auswählen — davon hängt alles Weitere ab.';
      else if (!form.objektart) e.objektart = 'Bitte die Art des Objekts wählen.';
      else if (istMfh && !form.gewerbeanteil) e.gewerbeanteil = 'Bitte auswählen.';
    }
    if (s === 2) {
      if (!/^\d{5}$/.test(form.plz)) e.plz = 'Bitte eine fünfstellige Postleitzahl angeben.';
      else if (!laedt && !orte.length) e.plz = 'Zu dieser Postleitzahl liegt uns keine Gemeinde vor.';
    }
    if (s === 3) {
      const bj = zahl(form.baujahr);
      const fl = zahl(form.wohnflaeche);
      if (!bj || bj < 1800 || bj > new Date().getFullYear() + 1) {
        e.baujahr = 'Bitte ein Baujahr zwischen 1800 und heute angeben.';
      }
      if (!(fl > 0)) e.wohnflaeche = 'Bitte die Fläche in m² angeben.';
      if (istMfh) {
        const we = zahl(form.einheiten);
        if (!(we >= 2)) {
          e.einheiten = 'Ein Mehrfamilienhaus hat mindestens 2 Wohnungen.';
        } else if (fl > 0) {
          const proWe = fl / we;
          if (proWe < QM_JE_WE.min) {
            e.wohnflaeche = `Das wären rechnerisch nur ${Math.round(proWe)} m² je Wohnung. `
              + 'Bitte die Summe aller Wohnungen eintragen, nicht die Fläche einer einzelnen.';
          } else if (proWe > QM_JE_WE.max) {
            e.wohnflaeche = `Das wären rechnerisch ${Math.round(proWe)} m² je Wohnung. `
              + (hatGewerbe ? 'Ist die Gewerbefläche versehentlich mit eingerechnet?'
                : 'Bitte Fläche und Anzahl der Wohnungen prüfen.');
          }
        }
      }
    }
    if (s === 4) {
      const fl = zahl(form.wohnflaeche);
      const m = zahl(form.kaltmiete);
      if (!(m > 0)) {
        e.kaltmiete = 'Bitte die monatliche Nettokaltmiete angeben.';
      } else if (fl > 0) {
        const proQm = m / fl;
        if (proQm < MIETE_JE_QM.min) {
          e.kaltmiete = `Das ergäbe ${qm(proQm)} €/m² — ungewöhnlich niedrig. `
            + (istMfh ? 'Bitte die Summe aller Wohnungen pro Monat eintragen.'
              : 'Bitte die Monatsmiete prüfen.');
        } else if (proQm > MIETE_JE_QM.max) {
          e.kaltmiete = `Das ergäbe ${qm(proQm)} €/m² — ungewöhnlich hoch. `
            + 'Ist versehentlich die Jahresmiete eingetragen?'
            + (hatGewerbe ? ' Oder die Gewerbemiete? Dafür gibt es ein eigenes Feld.' : '');
        }
      }
      if (!form.erhoehungArt) e.erhoehungArt = 'Bitte auswählen.';
      if (form.erhoehungArt === 'ein_bis_drei' && !form.erhoehungJahr) {
        e.erhoehungJahr = 'Bitte das ungefähre Jahr angeben.';
      }
      const pz = form.erhoehungProzent.trim();
      if (pz && (zahl(pz) < 0 || zahl(pz) > 100)) {
        e.erhoehungProzent = 'Bitte einen Wert zwischen 0 und 100 angeben.';
      }
    }
    if (s === 5) {
      if (!form.rolle) e.rolle = 'Bitte auswählen.';
      if (!form.vorname.trim()) e.vorname = 'Bitte ausfüllen.';
      if (!form.nachname.trim()) e.nachname = 'Bitte ausfüllen.';
      if (!EMAIL_RE.test(form.email.trim())) e.email = 'Gültige E-Mail-Adresse erforderlich.';
      // Für die Rückfragen zum Objekt brauchen wir eine Nummer.
      const tel = form.telefon.replace(/[^\d]/g, '');
      if (!tel) e.telefon = 'Bitte eine Telefonnummer angeben.';
      else if (tel.length < 6) e.telefon = 'Diese Nummer scheint unvollständig.';
    }
    return e;
  }

  const wurzel = useRef(null);
  const nachOben = () => {
    if (eingebettet && wurzel.current) {
      const ziel = wurzel.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: ziel, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reine Gewerbeobjekte lassen sich mit Wohnungsmieten nicht bewerten. Sie
  // werden nicht abgewiesen, sondern an Spezifika und Miete vorbei direkt zum
  // Kontakt geführt — die Einschätzung übernimmt dann ein Mensch.
  const pfad = istGewerbe ? [1, 2, 3, 5] : [1, 2, 3, 4, 5];
  const pfadPos = Math.max(1, pfad.indexOf(schritt) + 1);
  const letzterSchritt = schritt === pfad[pfad.length - 1];
  const weiterZu = (s) => pfad[Math.min(pfad.indexOf(s) + 1, pfad.length - 1)];
  const zurueckZu = (s) => pfad[Math.max(pfad.indexOf(s) - 1, 0)];

  function weiter() {
    const e = pruefe(schritt);
    setFehler(e);
    if (Object.keys(e).length) return;
    if (!gestartet.current) {
      gestartet.current = true;
      trackMeta('SubmitApplication', { customData: { content_name: 'Mietpreis-Analyse' } });
    }
    setSchritt(weiterZu);
    nachOben();
  }
  const zurueck = () => { setFehler({}); setSchritt(zurueckZu); nachOben(); };

  async function absenden(ev) {
    ev.preventDefault();
    const e = pruefe(5);
    setFehler(e);
    if (Object.keys(e).length) return;

    setStatus('sendet');
    const ortName = (orte.find((o) => o.ags === form.ags) || {}).name || '';
    // Die Strasse ist freiwillig — ohne sie darf kein Komma ins Leere zeigen.
    const objektAdresse = [form.strasse.trim(), `${form.plz} ${ortName}`.trim()]
      .filter(Boolean).join(', ');

    let r = null;
    if (!istGewerbe) {
      try {
        // Ein Aufruf: Der Server wertet aus, rendert den Bericht und schickt
        // ihn an die angegebene Adresse. Das Ergebnis kommt sofort zurück,
        // der Versand läuft dort im Hintergrund weiter.
        const antwort = await berichtAnfordern({
          ags: form.ags,
          bezirk: form.bezirk || null,
          objektart: form.objektart,
          gewerbeanteil: form.gewerbeanteil || 'kein',
          baujahr: zahl(form.baujahr) || null,
          einheiten: istMfh ? zahl(form.einheiten) : 1,
          wohnflaeche: zahl(form.wohnflaeche),
          kaltmiete: zahl(form.kaltmiete),
          letzte_erhoehung: {
            art: form.erhoehungArt || 'unbekannt',
            jahr: form.erhoehungJahr ? Number(form.erhoehungJahr) : null,
            prozent: form.erhoehungProzent || null,
          },
          adresse: objektAdresse,
          gewerbe: hatGewerbe ? {
            einheiten: zahl(form.gewerbeeinheiten) || null,
            flaeche: zahl(form.gewerbeflaeche) || null,
            miete: zahl(form.gewerbemiete) || null,
          } : null,
          kontakt: {
            anrede: form.anrede || null,
            vorname: form.vorname.trim(),
            nachname: form.nachname.trim(),
            email: form.email.trim(),
            telefon: form.telefon.trim() || null,
            rolle: form.rolle || null,
          },
        });
        r = antwort.ergebnis;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[Mietpreis-Analyse] Auswertung fehlgeschlagen:', err);
        setStatus('fehler');
        return;
      }
    }

    const art = [...UNTERARTEN.wohnen, ...UNTERARTEN.gewerbe]
      .find((u) => u.id === form.objektart);
    try {
      await sendLead({
        formType: 'Mietpreis-Analyse',
        replyTo: form.email.trim(),
        fields: {
          Objektart: art ? art.titel : '—',
          Gewerbeanteil: istMfh
            ? (GEWERBEANTEIL.find((g) => g.id === form.gewerbeanteil) || {}).titel || '—'
            : '—',
          Objekt: objektAdresse,
          Stadtbezirk: form.bezirk || '—',
          Baujahr: form.baujahr || '—',
          Wohneinheiten: istMfh ? (form.einheiten || '—') : '1',
          'Fläche (m²)': form.wohnflaeche || '—',
          Gewerbeeinheiten: hatGewerbe ? (form.gewerbeeinheiten || '—') : '—',
          'Gewerbefläche (m²)': hatGewerbe ? (form.gewerbeflaeche || '—') : '—',
          'Nettokaltmiete (€/Monat)': form.kaltmiete || '—',
          'Nettokaltmiete Gewerbe (€/Monat)': hatGewerbe ? (form.gewerbemiete || '—') : '—',
          'Letzte Mieterhöhung': (ERHOEHUNG.find((x) => x.id === form.erhoehungArt) || {}).titel || '—',
          'Jahr der Erhöhung': form.erhoehungJahr || '—',
          'Damals erhöht um': form.erhoehungProzent ? `${form.erhoehungProzent} %` : '—',
          Leerstand: form.leerstand || '0',
          'Ist-Miete je m²': r ? `${qm(r.ist.pro_qm)} €` : '—',
          Marktspanne: r ? `${qm(r.markt.von)} – ${qm(r.markt.bis)} €/m² (${r.genauigkeit.stufe})` : '—',
          'Potenzial (€/Monat)': r ? eur(r.potenzial) : '—',
          Rolle: form.rolle,
          Anrede: form.anrede || '—',
          Vorname: form.vorname.trim(),
          Nachname: form.nachname.trim(),
          'E-Mail': form.email.trim(),
          Telefon: form.telefon.trim(),
        },
      });
      trackMeta('CompleteRegistration', {
        userData: { email: form.email.trim(), phone: form.telefon },
        customData: { content_name: 'Mietpreis-Analyse' },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Mietpreis-Analyse] Versand fehlgeschlagen:', err);
    }

    setErgebnis(r || { leer: true, istGewerbe });
    setOrtAnzeige(ortName);
    setStatus('fertig');
    nachOben();
  }

  const fertig = status === 'fertig';

  // Die Gastseite bringt ihre eigene h1 mit; zwei davon waeren weder fuer
  // Suchmaschinen noch fuer Screenreader eine saubere Gliederung.
  const H = eingebettet ? 'h2' : 'h1';
  // Eingesetzt in eine andere Seite darf es kein <main> sein — die App haelt
  // schon eines, und zwei davon sind ungueltiges HTML.
  const Wurzel = eingebettet ? 'section' : 'main';

  return (
    <Wurzel
      ref={wurzel}
      className={`mpa ${fertig ? 'mpa--ergebnis' : 'mpa--funnel'}${eingebettet ? ' mpa--eingebettet' : ''}`}
    >
    {fertig ? (
      <Ergebnisseite r={ergebnis} form={form} ort={ortAnzeige} onAnfrage={startAnfrage} titelTag={H} />
    ) : (
      <>
        {/* Überschrift und Fragebogen stehen bewusst in EINER Section.
            Als zwei Sections addieren sich auf schmalen Geräten deren
            Innenabstände — die seitenweite Regel setzt dort 60-80 px mit
            !important, sodass zwischen Überschrift und Formular 120-160 px
            klafften, wo auf dem Desktop 16 px stehen. */}
        <section className={`shell mpa-kopf${eingebettet ? ' reveal' : ''}`}>
          <H className="mpa-title">Sind Ihre Mieten auf Marktniveau?</H>

          <div className="mpa-fortschritt">
            <div className="mpa-fortschritt-kopf">
              <span>Schritt {pfadPos} von {pfad.length}</span>
              <span>{SCHRITTE[schritt - 1]}</span>
            </div>
            <div className="mpa-balken">
              <div className="mpa-balken-fuell" style={{ width: `${(pfadPos / pfad.length) * 100}%` }} />
            </div>
          </div>

          <form className="mpa-form" onSubmit={absenden} noValidate>
            {schritt === 1 && (
              <Schritt titel="Wie wird Ihre Immobilie genutzt?">
                <Feld label="Nutzung" pflicht error={fehler.oberart}>
                  <Kacheln optionen={OBERARTEN} wert={form.oberart} onChange={oberartWaehlen} />
                </Feld>

                {form.oberart && (
                  <Feld
                    label={istGewerbe ? 'Was genau?' : 'Was genau?'}
                    pflicht error={fehler.objektart}
                  >
                    <Kacheln optionen={UNTERARTEN[form.oberart]}
                      wert={form.objektart} onChange={unterartWaehlen} />
                  </Feld>
                )}

                {istMfh && (
                  <Feld label="Gibt es Gewerbe im Haus?" pflicht error={fehler.gewerbeanteil}
                    hint="z.B. Laden, Büro oder Praxis im Erdgeschoss.">
                    <Wahl optionen={GEWERBEANTEIL} wert={form.gewerbeanteil}
                      onChange={setzen('gewerbeanteil')} />
                  </Feld>
                )}

                {hinweis && <Band {...hinweis} />}
              </Schritt>
            )}

            {schritt === 2 && (
              <Schritt titel="Wo steht Ihre Immobilie?">
                <Feld label="Straße und Hausnummer"
                  hint="Präzisiert das Ergebnis wenn möglich auf den Stadtteil.">
                  <input type="text" value={form.strasse} onChange={ausEvent('strasse')}
                    placeholder="Musterstraße 12" autoComplete="street-address" />
                </Feld>
                <div className="mpa-row">
                  <Feld label="Postleitzahl" pflicht error={fehler.plz}>
                    <input type="text" inputMode="numeric" maxLength={5} value={form.plz}
                      onChange={ausEvent('plz')} placeholder="60311" autoComplete="postal-code" />
                  </Feld>
                  <Feld label="Ort" hint={orte.length > 1 ? 'Diese PLZ liegt in mehreren Gemeinden.' : null}>
                    {orte.length > 1 ? (
                      <select value={form.ags} onChange={ausEvent('ags')}>
                        {orte.map((o) => <option key={o.ags} value={o.ags}>{o.name}</option>)}
                      </select>
                    ) : (
                      <input type="text" readOnly value={orte[0]?.name || (laedt ? 'wird geladen …' : '')}
                        placeholder="ergibt sich aus der PLZ" />
                    )}
                  </Feld>
                </div>
                {bezirksKandidaten.length > 0 && (
                  <Feld label="Stadtbezirk"
                    hint={
                      bezirkStatus === 'suche' ? 'Wird anhand der Straße ermittelt …'
                        : bezirkStatus?.bezirk
                          ? `Aus der Adresse ermittelt${bezirkStatus.ortsteil ? ` · Ortsteil ${bezirkStatus.ortsteil}` : ''}.`
                          : 'Bitte wählen — das schärft das Ergebnis deutlich.'
                    }>
                    <select value={form.bezirk} onChange={ausEvent('bezirk')}>
                      <option value="">stadtweiter Durchschnitt</option>
                      {bezirksKandidaten.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Feld>
                )}
              </Schritt>
            )}

            {schritt === 3 && (
              <Schritt titel="Eckdaten zu Ihrer Immobilie">
                <div className="mpa-row">
                  <Feld label="Baujahr ca." pflicht error={fehler.baujahr}>
                    <input type="text" inputMode="numeric" maxLength={4} value={form.baujahr}
                      onChange={ausEvent('baujahr')} placeholder="1968" />
                  </Feld>
                  {istMfh ? (
                    <Feld label="Wohnungen" pflicht error={fehler.einheiten}>
                      <input type="text" inputMode="numeric" value={form.einheiten}
                        onChange={ausEvent('einheiten')} placeholder="12" />
                    </Feld>
                  ) : (
                    <Feld label={flaechenLabel} pflicht error={fehler.wohnflaeche}>
                      <input type="text" inputMode="numeric" value={form.wohnflaeche}
                        onChange={ausEvent('wohnflaeche')} placeholder="75" />
                    </Feld>
                  )}
                </div>

                {istMfh && (
                  <Feld label={flaechenLabel} pflicht error={fehler.wohnflaeche}
                    hint="Summe aller Wohnungen in m², ohne Keller- & Nebenflächen.">
                    <input type="text" inputMode="numeric" value={form.wohnflaeche}
                      onChange={ausEvent('wohnflaeche')} placeholder="840" />
                  </Feld>
                )}

                {hatGewerbe && (
                  <div className="mpa-gewerbe">
                    <p className="mpa-gewerbe-kopf">Gewerbeanteil — fließt nicht in die Auswertung ein</p>
                    <div className="mpa-row">
                      <Feld label="Gewerbeeinheiten" hint="Optional.">
                        <input type="text" inputMode="numeric" value={form.gewerbeeinheiten}
                          onChange={ausEvent('gewerbeeinheiten')} placeholder="1" />
                      </Feld>
                      <Feld label="Gewerbefläche in m²" hint="Optional.">
                        <input type="text" inputMode="numeric" value={form.gewerbeflaeche}
                          onChange={ausEvent('gewerbeflaeche')} placeholder="120" />
                      </Feld>
                    </div>
                  </div>
                )}

                {istGewerbe && (
                  <Band ton="warn" kopf="Die Auswertung folgt nicht automatisch."
                    text="Wir nehmen die Angaben auf und melden uns mit einer persönlichen Einschätzung." />
                )}
              </Schritt>
            )}

            {schritt === 4 && (
              <Schritt titel="Wie viel Kaltmiete erzielt Ihre Immobilie?">
                <Feld
                  label={hatGewerbe ? 'Nettokaltmiete der Wohnungen pro Monat'
                    : 'Nettokaltmiete pro Monat'}
                  pflicht error={fehler.kaltmiete}
                  hint={proQmVorschau ? `Das entspricht ${qmE(proQmVorschau)} Fläche.`
                    : 'Ohne Neben- und Heizkosten.'}>
                  <div className="mpa-betrag">
                    <input type="text" inputMode="numeric" value={form.kaltmiete}
                      onChange={alsBetragSetzen('kaltmiete')} placeholder="9.800" />
                    <span className="mpa-betrag-einheit">€</span>
                  </div>
                </Feld>

                {hatGewerbe && (
                  <div className="mpa-gewerbe">
                    <p className="mpa-gewerbe-kopf">Gewerbeanteil — fließt nicht in die Auswertung ein</p>
                    <Feld label="Nettokaltmiete des Gewerbes pro Monat" hint="Optional.">
                      <div className="mpa-betrag">
                        <input type="text" inputMode="numeric" value={form.gewerbemiete}
                          onChange={alsBetragSetzen('gewerbemiete')} placeholder="1.400" />
                        <span className="mpa-betrag-einheit">€</span>
                      </div>
                    </Feld>
                  </div>
                )}

                <Feld label="Wann wurde die Miete zuletzt erhöht?" pflicht error={fehler.erhoehungArt}>
                  <Wahl optionen={ERHOEHUNG} wert={form.erhoehungArt}
                    onChange={setzen('erhoehungArt')} />
                </Feld>

                {form.erhoehungArt === 'ein_bis_drei' && (
                  <div className="mpa-row">
                    <Feld label="In welchem Jahr ungefähr?" pflicht error={fehler.erhoehungJahr}>
                      <select value={form.erhoehungJahr} onChange={ausEvent('erhoehungJahr')}>
                        <option value="">— Bitte wählen</option>
                        {JAHRE.map((j) => <option key={j} value={j}>{j}</option>)}
                      </select>
                    </Feld>
                    <Feld label="Um wie viel Prozent?" error={fehler.erhoehungProzent}
                      hint="Optional — schärft den Spielraum erheblich.">
                      <input type="text" inputMode="decimal" value={form.erhoehungProzent}
                        onChange={ausEvent('erhoehungProzent')} placeholder="z. B. 8" />
                    </Feld>
                  </div>
                )}

                {frist.im_fenster && (
                  <p className="mpa-band">
                    <strong>Das liegt im Dreijahresfenster.</strong> Eine Erhöhung aus den
                    letzten drei Jahren wird nach § 558 Abs. 3 BGB auf die Kappungsgrenze
                    angerechnet — sie verkleinert also den heutigen Spielraum.
                  </p>
                )}

                {istMfh && (
                  <Feld label="Davon leer stehend" hint="Anzahl der Wohnungen, optional.">
                    <input type="text" inputMode="numeric" value={form.leerstand}
                      onChange={ausEvent('leerstand')} placeholder="0" />
                  </Feld>
                )}
              </Schritt>
            )}

            {schritt === 5 && (
              <Schritt titel="Wohin dürfen wir den Bericht schicken?"
                text={istGewerbe
                  ? 'Wir sehen uns das Objekt an und melden uns mit einer persönlichen Einschätzung.'
                  : 'Sie sehen das Ergebnis direkt im Anschluss. Den ausführlichen Bericht schicken wir an die angegebene Adresse.'}>
                <Feld label="Sie sind" pflicht error={fehler.rolle}>
                  <Wahl optionen={ROLLE} wert={form.rolle} onChange={setzen('rolle')} />
                </Feld>
                <Feld label="Anrede">
                  <Wahl optionen={ANREDE} wert={form.anrede} onChange={setzen('anrede')} />
                </Feld>
                <div className="mpa-row">
                  <Feld label="Vorname" pflicht error={fehler.vorname}>
                    <input type="text" value={form.vorname} onChange={ausEvent('vorname')} autoComplete="given-name" />
                  </Feld>
                  <Feld label="Nachname" pflicht error={fehler.nachname}>
                    <input type="text" value={form.nachname} onChange={ausEvent('nachname')} autoComplete="family-name" />
                  </Feld>
                </div>
                <Feld label="E-Mail" pflicht error={fehler.email}>
                  <input type="email" value={form.email} onChange={ausEvent('email')} autoComplete="email" />
                </Feld>
                <Feld label="Telefon" pflicht error={fehler.telefon}>
                  <input type="tel" value={form.telefon} onChange={ausEvent('telefon')} autoComplete="tel" />
                </Feld>
                <p className="mpa-datenschutz">
                  Mit dem Absenden bestätigen Sie, dass Sie unsere <a href='/AGB' target='_blank'>AGB</a> und <a href="/datenschutz" target='_blank'>Datenschutzhinweise</a> gelesen haben und der Verarbeitung Ihrer Angaben und einer telefonischen und schriftlichen Kontaktaufnahme zur Bearbeitung Ihrer Anfrage zustimmen.
                </p>
              </Schritt>
            )}

            <div className="mpa-nav">
              {pfadPos > 1 && (
                <button type="button" className="btn-ghost" onClick={zurueck}>Zurück</button>
              )}
              {!letzterSchritt ? (
                <button type="button" className="btn-primary btn-primary--filled mpa-weiter"
                  onClick={weiter} disabled={laedt}>
                  {laedt ? 'einen Moment …' : 'Weiter'}
                </button>
              ) : (
                <button type="submit" className="btn-primary btn-primary--filled mpa-weiter"
                  disabled={status === 'sendet'}>
                  {status === 'sendet' ? 'wird ausgewertet …'
                    : istGewerbe ? 'Angaben absenden' : 'Auswertung ansehen'}
                </button>
              )}
            </div>

            {status === 'fehler' && (
              <p className="field-error" style={{ marginTop: 14 }}>
                Die Auswertung ist gerade nicht erreichbar. Bitte versuchen Sie es in einem
                Moment noch einmal.
              </p>
            )}
          </form>
        </section>
      </>
    )}
    </Wurzel>
  );
}

/**
 * Eigene Adresse: dasselbe Werkzeug, nur mit Seitenrahmen darum.
 */
export default function MietpreisAnalysePage() {
  // Kein eigener .homev2-Wrapper: Den haelt bereits die App. Ein zweiter wuerde
  // die dort gesetzten Variablen neu deklarieren — unter anderem --bar-h, das
  // auf dieser Seite gerade 0 sein soll, weil hier keine Hinweisleiste steht.
  return (
    <>
      <Seo
        title="hamba - Kostenlose Mietpreis-Analyse für Immobilien"
        description="In fünf kurzen Schritten sehen Sie, wo Ihre Mieten im örtlichen Marktvergleich stehen — inklusive Kappungsgrenze nach § 558 Abs. 3 BGB. Kostenlos und unverbindlich."
        path="/kostenlose-mietpreis-analyse"
      />
      {/* Dieselbe Navigation wie auf der Startseite: Diese Seite ist wie jene
          eine Einstiegsseite, und zwei gleich aussehende Umrisskaesten (Login
          und Erstanalyse) liessen dort keine Hauptaktion erkennen. */}
      <SiteNav />
      <MietpreisAnalyseTool />
      <MinimalFooter />
    </>
  );
}

function Schritt({ titel, text, children }) {
  return (
    <div className="mpa-schritt">
      <h2 className="mpa-h2">{titel}</h2>
      {text && <p className="mpa-schritt-text">{text}</p>}
      {children}
    </div>
  );
}

function Ergebnisseite({ r, form, ort, onAnfrage, titelTag: H = 'h1' }) {
  if (!r || r.leer) {
    return (
      <>
        <section className="shell mpa-hero">
          <p className="eyebrow">Vielen Dank</p>
          <H className="mpa-title mpa-title--ergebnis">Ihre Angaben sind bei uns eingegangen.</H>
          <p className="lede">
            {r?.istGewerbe
              ? 'Gewerbemieten lassen sich nicht aus einer Tabelle ablesen — Branche, Lauflage '
                + 'und Vertragslaufzeit entscheiden. Ein Fachmann von uns sieht sich das Objekt '
                + 'an und meldet sich mit einer Einschätzung.'
              : `Für ${ort} liegen uns keine auswertbaren Marktwerte vor. Wir sehen uns das `
                + 'Objekt manuell an und melden uns mit einer Einschätzung.'}
          </p>
        </section>
        <Erlaeuterung />
        <section className="shell mpa-disclaimer">
          <div className="mpa-cta">
            <p>Wenn es schneller gehen soll, sprechen wir direkt.</p>
            <button type="button" className="btn-primary" onClick={onAnfrage}>Erstanalyse anfragen</button>
          </div>
        </section>
      </>
    );
  }

  const { markt, spanne_monat: spanneMonat, ist, genauigkeit, gebaeude } = r;
  const feld = Math.max(markt.bis - markt.von, 0.01);
  const pos = Math.min(100, Math.max(0, ((ist.pro_qm - markt.von) / feld) * 100));
  const mehrere = gebaeude.einheiten > 1;

  return (
    <>
      <section className="shell mpa-hero">
        <H className="mpa-title mpa-title--ergebnis">
          {form.strasse ? `${form.strasse}, ` : ''}{form.plz} {ort}
        </H>
        <p className="lede">
          {r.objektart.label}
          {mehrere ? ` · ${gebaeude.einheiten} Wohnungen` : ''}
          {` · ${eur(gebaeude.flaeche)} m²`}
          {gebaeude.baujahr ? ` · Baujahr ${gebaeude.baujahr}` : ''}.
          {form.email ? ` Den ausführlichen Bericht haben wir an ${form.email} geschickt.` : ''}
        </p>
      </section>

      <section className="shell mpa-grid">
        {/* Die linke Spalte ist ein Stapel, damit der CTA-Kasten unmittelbar
            unter der Marktspanne sitzt. Als eigenes Grid-Kind rutschte er in
            die zweite Zeile — also unter beide Karten — und der Leerraum unter
            der kürzeren linken Karte bliebe stehen. */}
        <div className="mpa-spalte">
        <div className="mpa-karte">
          <div className="mpa-badge" data-rang={genauigkeit.rang}>
            {genauigkeit.stufe}{markt.gebiet ? ` · ${markt.gebiet}` : ''}
          </div>
          <p className="eyebrow">Marktspanne {r.segment === 'neubau' ? 'Neubau' : 'Bestand'}</p>
          <p className="mpa-spanne">
            {eur(spanneMonat.von)} – {eur(spanneMonat.bis)} €<span>pro Monat</span>
          </p>
          <p className="mpa-sub">
            entspricht {qm(markt.von)} – {qmE(markt.bis)} · Median {qmE(markt.median)}
            {r.spitze_monat ? ` · Spitzenangebote bis ${eurE(r.spitze_monat)}` : ''}
          </p>

          <div className="mpa-skala">
            <div className="mpa-skala-bar"><div className="mpa-skala-marke" style={{ left: `${pos}%` }} /></div>
            <div className="mpa-skala-enden">
              <span>{eurE(spanneMonat.von)}</span><span>{eurE(spanneMonat.bis)}</span>
            </div>
          </div>

          <div className="mpa-zeile">
            <span>{mehrere ? 'Ihre Mieteinnahmen' : 'Ihre Miete'}</span>
            <strong>{eurE(ist.monat)} · {qmE(ist.pro_qm)}</strong>
          </div>
          <div className="mpa-zeile">
            <span>Einordnung</span>
            <strong>
              {r.einordnung === 'unter' && 'unter der Marktspanne'}
              {r.einordnung === 'innerhalb' && 'innerhalb der Marktspanne'}
              {r.einordnung === 'ueber' && 'über der Marktspanne'}
            </strong>
          </div>
          {r.potenzial > 0 && (
            <div className="mpa-zeile mpa-zeile--stark">
              <span>Abstand zum Marktmedian</span>
              <strong>ca. +{eurE(r.potenzial)} / Monat</strong>
            </div>
          )}
        </div>

        <div className="mpa-karte mpa-cta-karte">
          <h2 className="mpa-h2">Zeit für die Umsetzung</h2>
          <p>
            Finden Sie heraus, was wir für Ihr Objekt tun können — und buchen Sie sich
            direkt ein Gespräch mit einem unserer Experten.
          </p>
          <button type="button" className="btn-primary btn-primary--filled" onClick={onAnfrage}>
            Gespräch vereinbaren 
          </button>
        </div>
        </div>

        <div className="mpa-karte">
          <h2 className="mpa-h2">Mögliche Erhöhung</h2>
          <p className="mpa-sub">
            {r.kappung.sicher
              ? `${r.kappung.gemeinde || 'Diese Gemeinde'}: höchstens ${r.kappung.prozent} % in drei Jahren. ${r.kappung.grund}`
              : `${r.kappung.grund} Beide Fälle im Vergleich:`}
          </p>
          <div className="mpa-tabelle-wrap">
            <table className="mpa-tabelle">
              <thead>
                <tr>
                  <th>Grenze</th>
                  <th>{r.szenarien.some((x) => x.verbraucht > 0) ? 'noch frei' : 'Höchstmiete'}</th>
                  <th>{r.szenarien.some((x) => x.verbraucht > 0) ? 'Höchstmiete' : 'Spielraum'}</th>
                  <th>je Jahr</th>
                </tr>
              </thead>
              <tbody>
                {r.szenarien.map((sz) => (
                  <tr key={sz.prozent}>
                    <td>
                      {sz.prozent} %
                      {sz.verbraucht > 0 && <span className="mpa-klein"> − {sz.verbraucht} %</span>}
                    </td>
                    {sz.verbraucht > 0
                      ? <><td>{sz.verfuegbar} %</td><td>{eurE(sz.obergrenze)}</td></>
                      : <><td>{eurE(sz.obergrenze)}</td><td>+ {eurE(sz.erreichbar - ist.monat)}</td></>}
                    <td>
                      {/* Ohne Angabe der damaligen Erhöhung ist der Wert eine
                          Obergrenze — das muss an der Zahl stehen. */}
                      {sz.unsicher && <span className="mpa-klein">höchstens </span>}
                      <strong>+ {eurE(sz.erreichbar_jahr)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mpa-fussnote">
            Gezeigt ist der kleinere Wert aus Kappungsgrenze und Marktniveau — mehr als der
            Markt hergibt, lässt sich nicht durchsetzen. Zusätzlich begrenzt die ortsübliche
            Vergleichsmiete die Erhöhung; sie ist hier nicht enthalten.
          </p>
          <ul className="mpa-hinweise">
            {r.hinweise.map((h, i) => <li key={i} data-art={h.art}>{h.text}</li>)}
          </ul>
        </div>
      </section>

      {/* Die Einordnung gehört zum Ergebnis, nicht vor den Fragebogen: Vorher
          erklärt sie Vorbehalte zu einer Zahl, die es noch gar nicht gibt. */}
      <Erlaeuterung />

      <section className="shell mpa-disclaimer">
        <div className="mpa-cta">
          <p>
            Der ausführlich Bericht ist auf dem Weg. Wenn Sie wissen wollen, was sich bei
            Ihrem Objekt tatsächlich erreichen lässt dann sprechen wir darüber.
          </p>
          <button type="button" className="btn-primary" onClick={onAnfrage}>Erstanalyse anfragen</button>
        </div>
      </section>
    </>
  );
}

function Erlaeuterung() {
  return (
    <section className="shell mpa-disclaimer">
      <h2 className="mpa-h2">Wie diese Analyse zu lesen ist</h2>
      <div className="mpa-disc-grid">
        <div>
          <h3>Basiert auf Angebotsmieten</h3>
          <p>
            Ausgewertet wird, was für vergleichbare Wohnungen am Markt <em>verlangt</em> wird.
            Das ist nicht die ortsübliche Vergleichsmiete des § 558 Abs. 2 BGB — eine
            Mieterhöhung lässt sich damit nicht rechtssicher begründen.
          </p>
        </div>
        <div>
          <h3>Mietpreisspanne statt exaktem Wert</h3>
          <p>
            Ausgegeben werden die mittleren 50 % der Angebote. Zustand, Ausstattung und
            Energieeffizienz verschieben den Wert innerhalb dieser Spanne erheblich.
          </p>
        </div>
        <div>
          <h3>Keine Rechtsberatung</h3>
          <p>
            Die Auswertung ist eine unverbindliche Orientierung und ersetzt weder eine
            Rechts- und Finanzberatung noch ein Wertgutachten. Ob und in welcher Höhe eine Mieterhöhung
            durchsetzbar ist, hängt vom Einzelfall und der gültigen Rechtssprechung ab.
          </p>
        </div>
      </div>
    </section>
  );
}
